import React from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CameraChip } from '@/components/Icons/CameraChip';
import { PaperclipChip } from '@/components/Icons/PaperclipChip';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useGalleryImage } from '@/hooks/useGalleryImage';
import { ROUTE_DETAILED_NEWS } from '@/router/routes';
import { NewsType, type NewsItem } from '@/types/news';
import { formatDate } from '@/utils/date';
import { stripHtml } from '@/utils/text';

interface Props {
  item: NewsItem;
}

const DEFAULT_TYPE: NewsType = NewsType.SystemInfo;

export const NewsCard: React.FC<Props> = ({ item }) => {
  const { t } = useTranslation();
  const imageUrl = useGalleryImage(item.titleImageId);

  const hasImage = !!item.titleImageId;
  const hasGallery = !!item.galleryId;
  const hasAttachments = item.attachments.length > 0;

  const typeChip = (
    <span className="rounded-2xl border border-transparent bg-constant-black px-3 py-1 text-caption-13 md:text-subhead-14 text-white contrast:border-border dark:bg-white dark:text-constant-black">
      {t(`home.news.types.${item.type ?? DEFAULT_TYPE}`)}
    </span>
  );
  const importantChip = item.isImportant && (
    <span className="rounded-2xl bg-warning px-3 py-1 text-caption-13 md:text-subhead-14 text-accent-foreground">
      {t('home.news.importantLabel')}
    </span>
  );

  if (hasImage) {
    return (
      <Link
        to={ROUTE_DETAILED_NEWS}
        state={{ news: item }}
        className="flex flex-col overflow-hidden rounded-2xl bg-card shadow-card md:min-h-[280px]"
      >
        <div className="relative w-full shrink-0 overflow-hidden bg-muted pt-[calc(150/370*100%)]">
          {imageUrl ? (
            <>
              <img
                src={imageUrl}
                alt=""
                draggable={false}
                className="absolute inset-0 size-full object-contain select-none"
              />
              <span className="absolute inset-0 bg-black/5" />
            </>
          ) : (
            <span className="absolute inset-0 flex items-center justify-center">
              <PageSpinner />
            </span>
          )}
          {(hasAttachments || hasGallery) && (
            <div className="absolute top-2 right-2 flex gap-1">
              {hasAttachments && <PaperclipChip />}
              {hasGallery && <CameraChip />}
            </div>
          )}
          <div className="absolute bottom-3 left-3 flex gap-2">
            {importantChip}
            {typeChip}
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-2 p-4">
          <h3 className="line-clamp-2 text-callout-16 text-foreground">
            {item.title}
          </h3>
          <div className="flex items-center gap-1.5 text-body-14 text-subtle-foreground">
            {item.isNew && <span className="size-1.5 rounded-full bg-accent" />}
            <span>{formatDate(item.lastModificationDate)}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={ROUTE_DETAILED_NEWS}
      state={{ news: item }}
      className="flex flex-col gap-4 rounded-2xl bg-card p-4 shadow-card md:min-h-[280px]"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {importantChip}
          {typeChip}
        </div>
        {(hasAttachments || hasGallery) && (
          <div className="flex shrink-0 gap-1">
            {hasAttachments && <PaperclipChip />}
            {hasGallery && <CameraChip />}
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 overflow-hidden">
        <h3 className="line-clamp-2 text-callout-16 text-foreground">
          {item.title}
        </h3>
        <div className="flex items-center gap-1.5 text-body-14 text-subtle-foreground">
          {item.isNew && <span className="size-1.5 rounded-full bg-accent" />}
          <span>{formatDate(item.lastModificationDate)}</span>
        </div>
        <p className="line-clamp-2 text-body-14 text-foreground md:line-clamp-4">
          {stripHtml(item.body)}
        </p>
      </div>
    </Link>
  );
};
